import { useCallback, useEffect, useRef, useState } from 'react';
import { describeError } from '../../services/errorMessage';
import {
    erneuereMaterialToken,
    ladeMaterialStand,
    setzeMaterialStatus,
    speichereMaterialArtikel,
    type MaterialStand,
} from '../../services/material/materialService';
import { ersetzeOderFuegeAn, offeneAnzahl, type ArtikelWerte, type StatusZiel } from '../../services/material/materialLogic';
import type { MaterialArtikel, MaterialMeldung } from '../../services/material/materialTypes';

/**
 * Daten und Schreibwege des Material-Bereichs.
 *
 * Geladen wird beim Öffnen, auf Knopfdruck und nach jeder Änderung — kein
 * Realtime: der Beschaffer arbeitet die Liste im eigenen Takt ab. Die Antwort
 * einer RPC wird sofort in die Liste übernommen; das Nachladen danach
 * bestätigt den Stand und meldet die Zahl offener Meldungen ans Menü
 * (`onOffeneGeaendert`).
 *
 * Kein setState im Effektrumpf (react-hooks/set-state-in-effect): „lädt“
 * ergibt sich daraus, dass die letzte Antwort zu einer älteren Ladenummer
 * gehört (Muster AdminApiLogsView). Eine überholte Antwort wird verworfen.
 *
 * Ein Ladefehler behält den letzten guten Stand und legt den Klartext
 * daneben. Die Schreibwege werfen `MaterialFehler` — der Aufrufer zeigt ihn
 * dort, wo geklickt wurde.
 */

interface Antwort {
    /** Zu welcher Ladenummer die Antwort gehört. */
    nr: number;
    stand: MaterialStand | null;
    nichtEingerichtet: boolean;
    fehler: string;
    /** Zeitpunkt des letzten geglückten Ladens (ms); Bezug für „vor X Tagen“. */
    standMs: number;
}

export interface MaterialZustand {
    stand: MaterialStand | null;
    /** SQL Nr. 178/179 fehlt in dieser Umgebung. */
    nichtEingerichtet: boolean;
    laedt: boolean;
    /** Klartext des letzten Ladefehlers, sonst ''. */
    ladeFehler: string;
    standMs: number;
    neuLaden: () => void;
    statusSetzen: (meldungId: string, ziel: StatusZiel) => Promise<MaterialMeldung>;
    artikelSpeichern: (id: string | null, werte: ArtikelWerte, aktiv: boolean) => Promise<MaterialArtikel>;
    tokenErneuern: (artikelId: string) => Promise<string>;
}

export const useMaterial = (onOffeneGeaendert?: (anzahl: number) => void): MaterialZustand => {
    const [neuladen, setNeuladen] = useState(0);
    const [antwort, setAntwort] = useState<Antwort | null>(null);

    const onOffeneRef = useRef(onOffeneGeaendert);
    useEffect(() => { onOffeneRef.current = onOffeneGeaendert; }, [onOffeneGeaendert]);

    useEffect(() => {
        let abgebrochen = false;
        const nr = neuladen;
        void ladeMaterialStand().then(
            (ergebnis) => {
                if (abgebrochen) return;
                if (ergebnis.art === 'nicht_eingerichtet') {
                    setAntwort({ nr, stand: null, nichtEingerichtet: true, fehler: '', standMs: Date.now() });
                    return;
                }
                setAntwort({ nr, stand: ergebnis.stand, nichtEingerichtet: false, fehler: '', standMs: Date.now() });
                onOffeneRef.current?.(offeneAnzahl(ergebnis.stand.meldungen));
            },
            (e: unknown) => {
                if (abgebrochen) return;
                setAntwort(alt => ({
                    nr,
                    stand: alt?.stand ?? null,
                    nichtEingerichtet: false,
                    fehler: describeError(e),
                    standMs: alt?.standMs ?? 0,
                }));
            },
        );
        return () => { abgebrochen = true; };
    }, [neuladen]);

    const neuLaden = useCallback(() => setNeuladen(n => n + 1), []);

    /** Antwort einer RPC sofort in den Stand übernehmen. */
    const lokal = useCallback((aendern: (s: MaterialStand) => MaterialStand) => {
        setAntwort(alt => (alt?.stand ? { ...alt, stand: aendern(alt.stand) } : alt));
    }, []);

    const statusSetzen = useCallback(async (meldungId: string, ziel: StatusZiel) => {
        const meldung = await setzeMaterialStatus(meldungId, ziel);
        lokal(s => ({ ...s, meldungen: ersetzeOderFuegeAn(s.meldungen, meldung) }));
        neuLaden();
        return meldung;
    }, [lokal, neuLaden]);

    const artikelSpeichern = useCallback(async (id: string | null, werte: ArtikelWerte, aktiv: boolean) => {
        const artikel = await speichereMaterialArtikel(id, werte, aktiv);
        lokal(s => ({ ...s, artikel: ersetzeOderFuegeAn(s.artikel, artikel) }));
        neuLaden();
        return artikel;
    }, [lokal, neuLaden]);

    const tokenErneuern = useCallback(async (artikelId: string) => {
        const token = await erneuereMaterialToken(artikelId);
        lokal(s => ({ ...s, artikel: s.artikel.map(a => (a.id === artikelId ? { ...a, qrToken: token } : a)) }));
        neuLaden();
        return token;
    }, [lokal, neuLaden]);

    return {
        stand: antwort?.stand ?? null,
        nichtEingerichtet: antwort?.nichtEingerichtet ?? false,
        laedt: antwort?.nr !== neuladen,
        ladeFehler: antwort?.fehler ?? '',
        standMs: antwort?.standMs ?? 0,
        neuLaden,
        statusSetzen,
        artikelSpeichern,
        tokenErneuern,
    };
};
